import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'

import GoogleMapReact from 'google-map-react'
import GooglePlacesAutocomplete from 'react-google-places-autocomplete'

import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from 'components/Common/Button'
import Dialog from '@mui/material/Dialog'
import CloseIcon from '@mui/icons-material/Close'
import MuiAlert from '@mui/material/Alert'
import ThreeDotsLoadingSpinner from 'components/Common/LoadingSpinner/threeDots'

import ConnectButton from 'components/Common/Button/ConnectButton/index'
import Overlay from 'components/Common/Overlay'
import ToolTip from 'components/Common/ToolTip'
import SectionTitle from 'components/Common/SectionTitle'
import ButtonTooltip from 'components/Common/ToolTip/ButtonTooltip'
import linkOffSvg from 'assets/link-off.svg'

import ReviewCard from './ReviewCard'
import { SECONDARY_BUTTON, DISABLED_BUTTON } from 'themes/theme'

const Card = styled(Box)(() => ({
  padding: '25px',
  gap: '20px',
  minHeight: '650px',
  height: '80vh',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-between',
}))

const CardHeader = styled(Box)(() => ({
  width: '100%',
  height: '30px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '20px',
}))

const DialogContent = styled(Box)(({ theme }) => ({
  width: {
    xs: '100%',
    sm: '300px',
    md: '400px',
    lg: '500px',
    xl: '500px',
  },
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  flexDirection: 'column',
  justifyContent: 'space-between',
  padding: '30px',
}))

const DialogActionBar = styled(Box)(() => ({
  width: '100%',
  gap: '2rem',
  marginTop: '2rem',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
}))

const PlaceMaker = ({ text }) => <div style={{ fontWeight: 'bold', fontSize: '1rem', width: '80px' }}>👉 {text}</div>

function GoogleReview() {
  const defaultMapProps = {
    center: {
      lat: 37.348768,
      lng: -121.947431,
    },
    zoom: 11,
  }

  const [loading, setLoading] = useState(false)
  const [map, setMap] = useState(null)
  const [placeDetails, setPlaceDetails] = useState(null)
  const [showOverlay, setShowOverlay] = useState(false)

  const [noReviews, setNoReviews] = useState(false)
  const [visibleDialog, setVisibleDialog] = useState(false)
  const [tempSelectedPlace, setTempSelectedPlace] = useState(JSON.parse(localStorage.getItem('GOOGLE_BZ_ID')))
  const [selectedPlace, setSelectedPlace] = useState(JSON.parse(localStorage.getItem('GOOGLE_BZ_ID')))

  const userInfo = useSelector(state => state.userReducer.user)

  const toggleDialog = () => {
    setTempSelectedPlace(selectedPlace)
    setVisibleDialog(!visibleDialog)
  }

  useEffect(() => {
    if (!tempSelectedPlace || !map) return
    const request = {
      placeId: tempSelectedPlace.value.place_id,
      fields: ['geometry'],
    }

    const service = new global.google.maps.places.PlacesService(map)

    service.getDetails(request, (place, status) => {
      map.setCenter({ lat: place.geometry.location.lat(), lng: place.geometry.location.lng() })
      map.setZoom(18)
    })
  }, [map, tempSelectedPlace])

  useEffect(() => {
    if (!selectedPlace || !map) return
    const request = {
      placeId: selectedPlace.value.place_id,
      fields: ['name', 'rating', 'reviews', 'geometry'],
    }

    const service = new global.google.maps.places.PlacesService(map)
    setLoading(true)
    service.getDetails(request, (place, status) => {
      if (!place.reviews || !place.reviews.length) {
        return
      }

      setPlaceDetails({
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
        reviews: place.reviews,
        rating: place.rating,
        name: place.name,
      })
      setLoading(false)
    })
  }, [map, selectedPlace])

  const onConnect = () => {
    const request = {
      placeId: tempSelectedPlace.value.place_id,
      fields: ['name', 'rating', 'reviews', 'geometry'],
    }

    const service = new global.google.maps.places.PlacesService(map)
    setLoading(true)
    service.getDetails(request, (place, status) => {
      map.setCenter({ lat: place.geometry.location.lat(), lng: place.geometry.location.lng() })
      map.setZoom(18)

      if (!place.reviews || !place.reviews.length) {
        setNoReviews(true)
        setLoading(false)
        // For closing the notification automatically
        setTimeout(() => {
          setNoReviews(false)
        }, 3000)
        return
      }

      localStorage.setItem('GOOGLE_BZ_ID', JSON.stringify(tempSelectedPlace))
      setSelectedPlace(tempSelectedPlace)
      setLoading(false)
      toggleDialog()
    })
  }

  const onDisconnect = () => {
    localStorage.setItem('GOOGLE_BZ_ID', null)
    setSelectedPlace(null)
    setPlaceDetails(null)
  }

  const handleApiLoaded = (map, maps) => {
    setMap(map)
  }

  const onUpdateLocation = e => {
    setTempSelectedPlace(e)
  }

  const createMapOptions = maps => {
    return {
      zoomControlOptions: {
        position: maps.ControlPosition.RIGHT_CENTER,
        style: maps.ZoomControlStyle.SMALL,
      },
      mapTypeControlOptions: {
        position: maps.ControlPosition.TOP_RIGHT,
      },
      mapTypeControl: true,
    }
  }

  const tooltipContent = (
    <Typography variant="body2" color="white">
      View recent customer reviews to stay on top of feedback.
    </Typography>
  )

  return (
    <Card onMouseOver={() => setShowOverlay(true)} onMouseLeave={() => setShowOverlay(false)}>
      <CardHeader>
        <div>
          <SectionTitle title="Google Reviews" />
          &nbsp;
          <ToolTip content={tooltipContent} />
        </div>
        <Box sx={{ display: 'flex' }}>
          {!!placeDetails && (
            <ButtonTooltip placement={'top'} title={'Disconnect App'} arrow onClick={onDisconnect}>
              <img
                src={linkOffSvg}
                height="20px"
                width="20px"
                alt={'google-review-disconnect-button'}
                style={{ cursor: 'pointer' }}
              />
            </ButtonTooltip>
          )}
        </Box>
      </CardHeader>
      {!!showOverlay && userInfo?.accountType === 'free' && (
        <Overlay>
          <Box
            sx={{
              height: 'calc(100% - 100px)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'space-around',
            }}
          >
            <Typography
              component="div"
              variant="body1B"
              color="white"
              sx={{
                width: '70%',
                textAlign: 'center',
                marginBottom: 2,
              }}
            >
              Reviews are so valuable to a business, as is keeping track of them so you can respond in a timely fashion.
              Connect your Yelp or Google Reviews accounts to use this tile to stay up to date on your latest reviews.
            </Typography>
          </Box>
        </Overlay>
      )}

      {!placeDetails ? (
        <Typography variant="body1" color="primary">
          Please enter your business name and location to see reviews.
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'flex',
            gap: '10px',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '520px',
            maxHeight: 'calc(100% - 100px)',
            overflowY: 'auto',
          }}
        >
          {!!loading && <ThreeDotsLoadingSpinner width="60px" />}

          {!loading &&
            userInfo?.accountType !== 'free' &&
            !!placeDetails &&
            placeDetails.reviews.map(review => (
              <ReviewCard
                isUnixTimeStamp={true}
                key={review.time}
                time={review.time}
                authorImageUrl={review.profile_photo_url}
                authorName={review.author_name}
                rating={review.rating}
                text={review.text}
              />
            ))}
        </Box>
      )}

      <ConnectButton
        onClick={toggleDialog}
        label={`Select ${!!loading || !!placeDetails ? 'different ' : 'your'} business`}
        fullWidth={true}
      />

      {!!visibleDialog && (
        <Dialog
          open={visibleDialog}
          onClose={toggleDialog}
          aria-labelledby="google-connect-dialog-title"
          aria-describedby="google-connect-dialog-description"
          sx={{ height: '92%', marginTop: '60px' }}
        >
          <DialogContent>
            <CloseIcon
              onClick={toggleDialog}
              fontSize="medium"
              sx={{
                cursor: 'pointer',
                position: 'absolute',
                top: '30px',
                right: '30px',
              }}
            />
            <Typography variant="h6" sx={{ textAlign: 'center', maxWidth: 'calc(100% - 60px)' }}>
              Input Business Name and Location Below
            </Typography>
            <Box component="div" variant="body1" sx={{ width: '100%' }}>
              <Typography component="div" variant="body2" color="primary">
                <GooglePlacesAutocomplete
                  selectProps={{
                    value: tempSelectedPlace,
                    placeholder: 'Type the location here',
                    onChange: onUpdateLocation,
                  }}
                />
              </Typography>
              <Box id="map" sx={{ height: '280px', width: '100%' }}>
                <GoogleMapReact
                  options={createMapOptions}
                  defaultCenter={defaultMapProps.center}
                  defaultZoom={defaultMapProps.zoom}
                  yesIWantToUseGoogleMapApiInternals
                  onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
                >
                  {!!placeDetails && (
                    <PlaceMaker lat={placeDetails.lat} lng={placeDetails.lng} text={placeDetails.name}></PlaceMaker>
                  )}
                </GoogleMapReact>
              </Box>
              {!!noReviews && (
                <MuiAlert severity="warning">
                  <Typography variant="caption">There is no review for this location. Please input again.</Typography>
                </MuiAlert>
              )}
            </Box>
            <DialogActionBar>
              <Button fullWidth negative onClick={toggleDialog} aria-label="closeModal">
                Close
              </Button>
              <Button
                fullWidth
                onClick={onConnect}
                aria-label="connectBusiness"
                type={!!tempSelectedPlace ? SECONDARY_BUTTON : DISABLED_BUTTON}
              >
                Connect
              </Button>
            </DialogActionBar>
          </DialogContent>
        </Dialog>
      )}
      {/**
       * NOTE: Just for holding the google map instance. Temporary object so don't need to show this
       */}
      <GoogleMapReact
        style={{ display: 'none' }}
        defaultCenter={defaultMapProps.center}
        defaultZoom={defaultMapProps.zoom}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
      ></GoogleMapReact>
    </Card>
  )
}

export default GoogleReview
